import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import cls from "./ClubMemberRequests.module.scss";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const statusMeta = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "accepted") return { label: "Accepted", tone: "good" };
  if (s === "rejected") return { label: "Rejected", tone: "bad" };
  return { label: "Pending", tone: "warn" };
};

const fmt = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString();
};

const safeText = (v) => (v == null ? "" : String(v));

const ClubMemberRequests = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [club, setClub] = useState(null);
  const [requests, setRequests] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  // ✅ compute safely (club is null at first render)
  const backToClubPath = useMemo(() => {
    if (!club?.cityId || !club?.streetId) return null;
    return `/admin/cities/single/${club.cityId}/streets/${club.streetId}/clubs/${clubId}`;
  }, [club?.cityId, club?.streetId, clubId]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);
        setErr("");

        // ✅ correct endpoint
        const { data } = await api.get(
          `/v1/admin/cities/clubs/${clubId}/membership-requests`,
        );

        if (!alive) return;

        setClub(data?.club || null);
        setRequests(Array.isArray(data?.requests) ? data.requests : []);
      } catch (e) {
        if (!alive) return;
        setErr(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load membership requests",
        );
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (clubId) run();

    return () => {
      alive = false;
    };
  }, [clubId]);

  const counts = useMemo(() => {
    const base = { all: requests.length, pending: 0, accepted: 0, rejected: 0 };
    for (const r of requests) {
      const s = String(r?.status || "pending").toLowerCase();
      if (s === "accepted") base.accepted += 1;
      else if (s === "rejected") base.rejected += 1;
      else base.pending += 1;
    }
    return base;
  }, [requests]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return requests
      .filter((r) => {
        const s = String(r?.status || "pending").toLowerCase();
        if (status !== "all" && s !== status) return false;
        if (!needle) return true;

        const senderName = safeText(r?.sender?.username).toLowerCase();
        const msg = safeText(r?.message).toLowerCase();
        const decidedBy = safeText(r?.decidedBy?.username).toLowerCase();

        return (
          senderName.includes(needle) ||
          msg.includes(needle) ||
          decidedBy.includes(needle) ||
          safeText(r?.clubJoinRequestId).toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => {
        const ad = new Date(a?.lastUpdatedAt || a?.createdAt || 0).getTime();
        const bd = new Date(b?.lastUpdatedAt || b?.createdAt || 0).getTime();
        return bd - ad;
      });
  }, [requests, q, status]);

  const onBackToClub = () => {
    // ✅ no crash if ids not loaded yet
    if (backToClubPath) navigate(backToClubPath);
  };

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Header */}
        <div className={cls.header}>
          <div className={cls.headerTop}>
            <div className={cls.titleWrap}>
              <h1 className={cls.title}>Membership Requests</h1>
              <p className={cls.subTitle}>
                {club?.clubName ? (
                  <>
                    Club: <span className={cls.clubName}>{club.clubName}</span>
                  </>
                ) : (
                  <>
                    Club ID: <span className={cls.clubName}>{clubId}</span>
                  </>
                )}
              </p>
            </div>

            <div className={cls.headerActions}>
              <button
                type="button"
                className={`${cls.btn} ${cls.btnGhost}`}
                onClick={onBackToClub}
                disabled={!backToClubPath}
                title={!backToClubPath ? "Loading club path…" : "Back to club"}
                style={
                  !backToClubPath
                    ? { opacity: 0.6, cursor: "not-allowed" }
                    : undefined
                }
              >
                Back to Club
              </button>

              <button
                type="button"
                className={`${cls.btn} ${cls.btnSoft}`}
                onClick={() => window.location.reload()}
                title="Refresh"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className={cls.filters}>
            <div className={cls.searchBox}>
              <span className={cls.searchIcon} aria-hidden="true">
                ⌕
              </span>
              <input
                className={cls.searchInput}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by username, message, id, decidedBy…"
                autoComplete="off"
              />
              {q ? (
                <button
                  type="button"
                  className={cls.clearBtn}
                  onClick={() => setQ("")}
                  aria-label="Clear search"
                  title="Clear"
                >
                  ×
                </button>
              ) : null}
            </div>

            <div className={cls.statusTabs}>
              {[
                ["all", `All (${counts.all})`],
                ["pending", `Pending (${counts.pending})`],
                ["accepted", `Accepted (${counts.accepted})`],
                ["rejected", `Rejected (${counts.rejected})`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`${cls.tab} ${
                    status === key ? cls.tabActive : ""
                  }`}
                  onClick={() => setStatus(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className={cls.body}>
          <AnimatePresence mode="wait">
            {loading ? (
              <Motion.div
                key="loading"
                className={cls.stateBox}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className={cls.loaderOrb} />
                <div className={cls.stateTitle}>Loading requests…</div>
                <div className={cls.stateHint}>
                  Fetching latest club activity
                </div>
              </Motion.div>
            ) : err ? (
              <Motion.div
                key="error"
                className={`${cls.stateBox} ${cls.stateError}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className={cls.stateTitle}>Something went wrong</div>
                <div className={cls.stateHint}>{err}</div>
                <button
                  type="button"
                  className={`${cls.btn} ${cls.btnPrimary}`}
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </Motion.div>
            ) : filtered.length === 0 ? (
              <Motion.div
                key="empty"
                className={cls.stateBox}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className={cls.stateTitle}>No requests found</div>
                <div className={cls.stateHint}>
                  Try changing the filter or search keywords.
                </div>
              </Motion.div>
            ) : (
              <Motion.div
                key="grid"
                className={cls.grid}
                initial="hidden"
                animate="show"
                variants={{
                  hidden: { opacity: 0 },
                  show: {
                    opacity: 1,
                    transition: { staggerChildren: 0.04, delayChildren: 0.03 },
                  },
                }}
              >
                {filtered.map((r) => {
                  const meta = statusMeta(r?.status);
                  const to = `/admin/clubs/single/${clubId}/membership-requests/${r.clubJoinRequestId}`;

                  return (
                    <Motion.div key={r.clubJoinRequestId} variants={fadeUp}>
                      <Link to={to} className={cls.card}>
                        <div className={cls.cardTop}>
                          <div className={cls.avatarWrap}>
                            {r?.sender?.avatar?.url ? (
                              <img
                                src={r.sender.avatar.url}
                                alt={r?.sender?.username || "Member"}
                                className={cls.avatar}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className={cls.avatarFallback}>
                                {safeText(r?.sender?.username || "U")
                                  .slice(0, 1)
                                  .toUpperCase()}
                              </div>
                            )}

                            <span
                              className={`${cls.statusPill} ${
                                meta.tone === "good"
                                  ? cls.pillGood
                                  : meta.tone === "bad"
                                    ? cls.pillBad
                                    : cls.pillWarn
                              }`}
                            >
                              {meta.label}
                            </span>
                          </div>

                          <div className={cls.cardHeadText}>
                            <div className={cls.nameRow}>
                              <div className={cls.username}>
                                {r?.sender?.username || "Unknown user"}
                              </div>
                              <div className={cls.reqId}>
                                #{String(r?.clubJoinRequestId).slice(-8)}
                              </div>
                            </div>

                            <div className={cls.miniLine}>
                              <span className={cls.dot} />
                              <span className={cls.miniLabel}>
                                Updated:
                              </span>{" "}
                              <span className={cls.miniValue}>
                                {fmt(r?.lastUpdatedAt)}
                              </span>
                            </div>
                          </div>

                          <div className={cls.chev} aria-hidden="true">
                            ›
                          </div>
                        </div>

                        <div className={cls.message}>
                          {r?.message ? r.message : "No message provided."}
                        </div>

                        <div className={cls.cardBottom}>
                          <div className={cls.metaGrid}>
                            <div className={cls.metaItem}>
                              <div className={cls.metaKey}>Decided By</div>
                              <div className={cls.metaVal}>
                                {r?.decidedBy?.username
                                  ? r.decidedBy.username
                                  : "—"}
                              </div>
                            </div>

                            <div className={cls.metaItem}>
                              <div className={cls.metaKey}>Decided At</div>
                              <div className={cls.metaVal}>
                                {r?.decidedAt ? fmt(r.decidedAt) : "—"}
                              </div>
                            </div>
                          </div>

                          <div className={cls.openHint}>
                            Open request details
                          </div>
                        </div>
                      </Link>
                    </Motion.div>
                  );
                })}
              </Motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ClubMemberRequests;
